import { makeObservable, observable } from 'mobx';
import { BaseStore } from './baseStore';
import { RootStore } from './rootStore';
import { User } from '/src/lib/models';
import { MenuItemType, TMenu, TMenuId, URLString, UserRole } from '/src/lib/types';

export interface MenuStoreProps {
  /**
   * Dropdown menus to display on the page Header
   */
  menus: Record<TMenuId, TMenu>;
  /**
   * List of Roles that have access to the menu items
   */
  roles: UserRole[];
  /**
   * Public menu items are viewable by anyone visiting the page. If set to false, a user must be logged in to view the menu.
   */
  isPublic: boolean;
}

export class MenuStore extends BaseStore implements MenuStoreProps {
  menus: Record<TMenuId, TMenu> = {};
  roles: UserRole[] = [UserRole.Admin];
  isPublic = false;

  constructor(rootStore: RootStore) {
    super(rootStore);

    makeObservable(this, {
      menus: observable,
      roles: observable,
      isPublic: observable,
    });

    // Add menu
    this.addMenu('topbar');
  }

  /**
   * Validates that the given User has permissions to view this menu item.
   *
   * @param user - The User to verify
   * @returns True if the User is allowed to view the menu
   */
  shouldRender = (user?: User) => {
    return user
      ? !!~this.roles.indexOf('*' as UserRole) || !!user.roles.find((role) => this.roles.includes(role))
      : this.isPublic;
  };

  /**
   * Checks whether a menu exists.
   *
   * @param menuId - The Menu ID to check
   * @returns Boolean indicating the result
   */
  validateMenuExistence = (menuId: TMenuId) => {
    if (this.menus[menuId]) {
      return true;
    } else {
      throw new Error('TMenu does not exist');
    }
  };

  /**
   * Fetches a Header menu by ID
   *
   * @param menuId - ID of the menu to retrieve
   * @returns The menu, or undefined if it doesn't exist
   */
  getMenu = (menuId: TMenuId) => {
    // Validate that the menu exists
    this.validateMenuExistence(menuId);

    // Return the menu object
    return this.menus[menuId];
  };

  /**
   * Add a new menu to the page Header.
   *
   * @param menuId - ID of the new Menu item
   * @param isPublic - Whether to display the item to non-logged-in Users
   * @param roles - User roles allowed to view this menu
   * @returns The new menu
   */
  addMenu = (menuId: TMenuId, isPublic?: boolean, roles?: UserRole[]) => {
    // Create the new menu
    this.menus[menuId] = {
      isPublic: isPublic || false,
      roles: roles || Object.values(UserRole),
      items: [],
      shouldRender: this.shouldRender,
    };

    // Return the menu object
    return this.menus[menuId];
  };

  /**
   * Removes a menu from the page Header.
   *
   * @param menuId - ID of the menu to remove
   * @returns The removed menu
   */
  removeMenu = (menuId: TMenuId) => {
    // Validate that the menu exists
    this.validateMenuExistence(menuId);

    // Return the menu object
    delete this.menus[menuId];
  };

  /**
   * Add a menu item to the page Header.
   *
   * @param menuId - ID of the parent menu
   * @param menuItemTitle - Menu item title
   * @param menuItemURL - Navigate to this URL on menu item click
   * @param menuItemType - Menu item type
   * @param menuItemUIRoute - Navigate to this route on menu item click
   * @param isPublic - Whether to display the menu item to non-logged-in Users
   * @param roles - User roles allowed to view this menu item
   * @param position - This menu item's index in the parent menu
   * @returns
   */
  addMenuItem = (
    menuId: TMenuId,
    menuItemTitle: string,
    menuItemURL: URLString,
    menuItemType: MenuItemType,
    menuItemUIRoute: URLString,
    isPublic?: boolean,
    roles?: UserRole[],
    position?: number
  ) => {
    // Validate that the menu exists
    this.validateMenuExistence(menuId);

    // Push new menu item
    this.menus[menuId].items.push({
      title: menuItemTitle,
      link: menuItemURL,
      menuItemType: menuItemType || 'item',
      menuItemClass: menuItemType,
      uiRoute: menuItemUIRoute || '/' + menuItemURL,
      isPublic: isPublic === null || typeof isPublic === 'undefined' ? this.menus[menuId].isPublic : isPublic,
      roles: roles === null || typeof roles === 'undefined' ? this.menus[menuId].roles : roles,
      position: position || 0,
      items: [],
      open: false,
      anchor: null,
      shouldRender: this.shouldRender,
    });

    // Return the menu object
    return this.menus[menuId];
  };

  /**
   * Add a submenu item to the page Header.
   *
   * @param menuId - ID of the parent menu
   * @param rootMenuItemURL - URL of the parent menu item
   * @param menuItemTitle - Submenu title
   * @param menuItemURL - Navigate to this URL on submenu click
   * @param menuItemUIRoute - Navigate to this route on submenu click
   * @param isPublic - Whether to display the item to non-logged-in Users
   * @param roles - User roles allowed to view this menu
   * @param position - This submenu's index in the parent menu
   * @returns The updated menu
   */
  addSubMenuItem = (
    menuId: TMenuId,
    rootMenuItemURL: URLString,
    menuItemTitle: string,
    menuItemURL: URLString,
    menuItemUIRoute?: URLString,
    isPublic?: boolean,
    roles?: UserRole[],
    position?: number
  ) => {
    // Validate that the menu exists
    this.validateMenuExistence(menuId);

    // Search for menu item
    for (const itemIndex in this.menus[menuId].items) {
      if (this.menus[menuId].items[itemIndex].link === rootMenuItemURL) {
        // Push new submenu item
        this.menus[menuId].items[itemIndex].items.push({
          title: menuItemTitle,
          link: menuItemURL,
          uiRoute: menuItemUIRoute || '/' + menuItemURL,
          isPublic:
            isPublic === null || typeof isPublic === 'undefined'
              ? this.menus[menuId].items[itemIndex].isPublic
              : isPublic,
          roles: roles === null || typeof roles === 'undefined' ? this.menus[menuId].items[itemIndex].roles : roles,
          position: position || 0,
          shouldRender: this.shouldRender,
        });
      }
    }

    // Return the menu object
    return this.menus[menuId];
  };

  /**
   * Removes a menu item.
   *
   * @param menuId - Parent menu ID
   * @param menuItemURL - URL of the menu item to remove
   * @returns The removed menu item
   */
  removeMenuItem = (menuId: TMenuId, menuItemURL: URLString) => {
    // Validate that the menu exists
    this.validateMenuExistence(menuId);

    // Search for menu item to remove
    const itemIndex = this.menus[menuId].items.findIndex((item) => item.link === menuItemURL);
    this.menus[menuId].items.splice(itemIndex, 1);

    // Return the menu object
    return this.menus[menuId];
  };

  /**
   * Removes a submenu item.
   *
   * @param menuId - Parent menu ID
   * @param submenuItemURL - URL of the submenu item to remove
   * @returns The removed menu item
   */
  removeSubMenuItem = (menuId: TMenuId, submenuItemURL: URLString) => {
    // Validate that the menu exists
    this.validateMenuExistence(menuId);

    // Search for menu item to remove
    for (const itemIndex in this.menus[menuId].items) {
      for (const subitemIndex in this.menus[menuId].items[itemIndex].items) {
        if (this.menus[menuId].items[itemIndex].items[subitemIndex].link === submenuItemURL) {
          this.menus[menuId].items[itemIndex].items.splice(parseInt(subitemIndex), 1);
        }
      }
    }

    // Return the menu object
    return this.menus[menuId];
  };
}
